import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FiArrowLeft, FiUser, FiMail, FiPhone, FiMapPin, FiBriefcase, FiCheckCircle, FiXCircle, FiInfo } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getAdminUserById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import { getAdminRoleDetails } from '../../constants/adminRoles';
import RoleDetailsModal from '../../components/Admin/RoleDetailsModal';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children || 'N/A'}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminUserDetail = () => {
    const navigate = useNavigate();
    const { userId } = useParams();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [showRoleDetails, setShowRoleDetails] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) return;
            
            try {
                setLoading(true);
                setError(null);
                const data = await getAdminUserById(userId);
                // API returns { user: {...} }
                setUser(data?.user || data || null);
            } catch (e) {
                setError(handleApiError(e, t('pages.adminUserDetail.errorLoading')));
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    if (loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.adminUserDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/management')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">{t('pages.adminUserDetail.loading')}</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.adminUserDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/management')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <Alert variant="danger">{error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.adminUserDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/management')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <p className="text-muted">{t('pages.adminUserDetail.notFound')}</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const roleDetails = getAdminRoleDetails(user.role);

    return (
        <div className="dashboard-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiUser className="me-2" />
                                Détail de l'utilisateur admin
                            </h2>
                            <p className="text-muted mb-0">Code: {user.user_code || 'N/A'}</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/management')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                    </div>

                    {/* Informations principales */}
                    <Section title={t('pages.adminUserDetail.mainInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.userCode')}</Label>
                                <Value>
                                    <code style={{ fontSize: '0.9rem', color: 'black' }}>{user.user_code || 'N/A'}</code>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>{t('pages.adminUsers.fullName')}</Label>
                                <Value>
                                    <FiUser className="me-1" />
                                    {user.first_name || ''} {user.last_name || ''}
                                </Value>
                            </Col>
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.role')}</Label>
                                <Value>
                                    <span className="role-details-inline">
                                        <Badge bg="info">{roleDetails?.label || user.role || 'N/A'}</Badge>
                                        {roleDetails && (
                                            <Button
                                                type="button"
                                                variant="link"
                                                size="sm"
                                                className="p-0"
                                                onClick={() => setShowRoleDetails(true)}
                                                aria-label={`Voir les détails du rôle ${roleDetails.label}`}
                                            >
                                                <FiInfo className="me-1" aria-hidden="true" />
                                                Voir les détails
                                            </Button>
                                        )}
                                    </span>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.verificationStatus')}</Label>
                                <Value>
                                    {user.is_verified ? (
                                        <Badge bg="success">
                                            <FiCheckCircle className="me-1" />
                                            {t('pages.adminUserDetail.verified')}
                                        </Badge>
                                    ) : (
                                        <Badge bg="warning">
                                            <FiXCircle className="me-1" />
                                            {t('pages.adminUserDetail.notVerified')}
                                        </Badge>
                                    )}
                                </Value>
                            </Col>
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.accountActivated')}</Label>
                                <Value>
                                    {user.activate_account ? (
                                        <Badge bg="success">
                                            <FiCheckCircle className="me-1" />
                                            {t('pages.adminUserDetail.activated')}
                                        </Badge>
                                    ) : (
                                        <Badge bg="danger">
                                            <FiXCircle className="me-1" />
                                            {t('pages.adminUserDetail.deactivated')}
                                        </Badge>
                                    )}
                                </Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations de contact */}
                    <Section title={t('pages.adminUserDetail.contactInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.email')}</Label>
                                <Value>
                                    <FiMail className="me-1" />
                                    {user.email || 'N/A'}
                                </Value>
                            </Col>
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.phone')}</Label>
                                <Value>
                                    {user.phone_number ? (
                                        <>
                                            <FiPhone className="me-1" />
                                            {user.phone_number}
                                        </>
                                    ) : (
                                        'N/A'
                                    )}
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.countryCode')}</Label>
                                <Value>{user.country_code || 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations d'adresse */}
                    <Section title={t('pages.adminUserDetail.addressInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.addressField')}</Label>
                                <Value>
                                    {user.user_address ? (
                                        <>
                                            <FiMapPin className="me-1" />
                                            {user.user_address}
                                        </>
                                    ) : (
                                        'N/A'
                                    )}
                                </Value>
                            </Col>
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.zipCode')}</Label>
                                <Value>{user.zip_code || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.city')}</Label>
                                <Value>{user.city || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>{t('pages.adminUserDetail.country')}</Label>
                                <Value>{user.country || 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations entreprise */}
                    {user.company_name && (
                        <Section title={t('pages.adminUserDetail.companyInfo')}>
                            <Row>
                                <Col md={6}>
                                    <Label>{t('pages.adminUserDetail.company')}</Label>
                                    <Value>
                                        <FiBriefcase className="me-1" />
                                        {user.company_name}
                                    </Value>
                                </Col>
                                <Col md={6}>
                                    <Label>{t('pages.adminUserDetail.commission')}</Label>
                                    <Value>{user.commission || 'N/A'}</Value>
                                </Col>
                            </Row>
                            {user.legal_info && (
                                <Row className="mt-3">
                                    <Col md={12}>
                                        <Label>{t('pages.adminUserDetail.legalInfo')}</Label>
                                        <Value style={{ whiteSpace: 'pre-wrap' }}>{user.legal_info}</Value>
                                    </Col>
                                </Row>
                            )}
                        </Section>
                    )}
                </Card.Body>
            </Card>

            <RoleDetailsModal
                show={showRoleDetails}
                role={user.role}
                onHide={() => setShowRoleDetails(false)}
            />
        </div>
    );
};

export default AdminUserDetail;
